import React, { useEffect, useRef, useState } from 'react';
import TopoBackground from './TopoBackground';

type Status = 'arrived' | 'departed';
type Mood = 'great' | 'okay' | 'not_good' | 'skip';
type Step = 'choose' | 'form' | 'confirmed';


const GUEST_NAME_COOKIE = 'corazon_guest_name';
const GUEST_NAME_MAX_LENGTH = 80;
const GUEST_EMAIL_MAX_LENGTH = 254;
const GUEST_NAME_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;
const STATUS_DEVICE_COOKIE = 'corazon_status_device';
const STATUS_DEVICE_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;
const STATUS_DEVICE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const readCookieValue = (name: string) => {
  const prefix = `${name}=`;
  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  if (!entry) return '';
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    return '';
  }
};

const saveStatusDeviceCookie = (deviceId: string) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${STATUS_DEVICE_COOKIE}=${encodeURIComponent(deviceId)}; Max-Age=${STATUS_DEVICE_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
};

const ensureStatusDeviceCookie = () => {
  const existing = readCookieValue(STATUS_DEVICE_COOKIE);
  if (STATUS_DEVICE_ID_PATTERN.test(existing)) return existing;

  const deviceId = crypto.randomUUID();
  saveStatusDeviceCookie(deviceId);
  return deviceId;
};

const normalizeGuestName = (value: string) =>
  value.trim().replace(/\s+/g, ' ').slice(0, GUEST_NAME_MAX_LENGTH) || 'Guest';

const readGuestNameCookie = () => {
  const cookiePrefix = `${GUEST_NAME_COOKIE}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(cookiePrefix));

  if (!cookie) return 'Guest';

  try {
    return normalizeGuestName(decodeURIComponent(cookie.slice(cookiePrefix.length)));
  } catch {
    return 'Guest';
  }
};

const saveGuestNameCookie = (guestName: string) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${GUEST_NAME_COOKIE}=${encodeURIComponent(guestName)}; Max-Age=${GUEST_NAME_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
};

const MOOD_OPTIONS: { value: Mood; emoji: string; label: string }[] = [
  { value: 'great', emoji: '🙂', label: 'Great' },
  { value: 'okay', emoji: '😐', label: 'Okay' },
  { value: 'not_good', emoji: '🙁', label: 'Not good' },
];

const CHECK_IN_MOOD_OPTIONS: { value: Mood; emoji: string; label: string }[] = [
  ...MOOD_OPTIONS,
  { value: 'skip', emoji: '—', label: 'Skip' },
];

async function postGuestStatus(body: Record<string, unknown>) {
  const response = await fetch('/api/guest-status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  });

  if (!response.ok) {
    throw new Error(`Guest status notification failed with ${response.status}`);
  }
}

const formatDateTime = (date: Date) =>
  `${date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })} · ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;

const FORM_COPY: Record<Status, { eyebrow: string; title: React.ReactNode; moodLegend: string; submit: string; pin: string }> = {
  arrived: {
    eyebrow: 'Check-in',
    title: <>Welcome to <em>Corazón Townhouse.</em></>,
    moodLegend: 'How did arrival go?',
    submit: 'Check In',
    pin: 'Checking in · Capitol Hill',
  },
  departed: {
    eyebrow: 'Check-out',
    title: <>Thanks for <em>staying with us.</em></>,
    moodLegend: 'How was your stay?',
    submit: 'Check Out',
    pin: 'Checking out · Capitol Hill',
  },
};

type OfferState = 'idle' | 'sending' | 'sent' | 'already' | 'error';

// "Coming back?" card on the done screen: the guest can ask for a one-time
// email with 15% off their next stay. /api/next-stay-offer sends it once per
// email address, so asking at check-in and again at check-out is harmless.
const NextStayOffer: React.FC<{ status: Status; name: string; initialEmail: string }> = ({ status, name, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail);
  const [state, setState] = useState<OfferState>('idle');
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const address = email.trim().toLowerCase();
    if (!address || state === 'sending') return;
    setState('sending');
    setError('');
    try {
      const response = await fetch('/api/next-stay-offer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: address, name, status }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setSentTo(address);
        setState(data.alreadySent ? 'already' : 'sent');
      } else {
        setState('error');
        setError(data.error || 'We couldn’t send it. Please try again.');
      }
    } catch {
      setState('error');
      setError('Network error. Please try again.');
    }
  };

  return (
    <section className="status-offer" aria-labelledby="status-offer-title">
      <p className="status-offer-eyebrow">Coming back?</p>
      <h2 id="status-offer-title">15% off your next stay</h2>
      {state === 'sent' || state === 'already' ? (
        <p className="status-offer-done" role="status">
          <span aria-hidden="true">✓</span>
          <span className="status-offer-done-text">
            {state === 'sent' ? (
              <>Sent! Check <strong>{sentTo}</strong> for your 15% link.</>
            ) : (
              <>You&rsquo;re all set — we already sent your 15% link to <strong>{sentTo}</strong>.</>
            )}
          </span>
        </p>
      ) : (
        <form onSubmit={submit}>
          <p className="status-offer-hint" id="status-offer-hint">
            Leave your email and we&rsquo;ll send a direct-booking link. Just one email — no mailing list.
          </p>
          <label className="sr-only" htmlFor="status-offer-email">Email</label>
          <input
            id="status-offer-email"
            name="offerEmail"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-describedby="status-offer-hint"
            maxLength={GUEST_EMAIL_MAX_LENGTH}
            required
          />
          {state === 'error' && <p className="status-offer-error" role="alert">{error}</p>}
          <button type="submit" className="status-submit" disabled={state === 'sending'}>
            {state === 'sending' ? 'Sending…' : 'Send my 15% link'}
          </button>
        </form>
      )}
    </section>
  );
};

interface GuestStatusProps {
  onReturnHome?: () => void;
}

// "Topo map" status page: lavender contour background shared with the
// homepage hero, two large choice cards, and one form (name, optional email
// to stay connected, mood) used for both check-in and check-out. No mailing list.
const GuestStatus: React.FC<GuestStatusProps> = ({
  onReturnHome = () => window.location.assign('/'),
}) => {
  const [step, setStep] = useState<Step>('choose');
  const [status, setStatus] = useState<Status | null>(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [mood, setMood] = useState<Mood>('great');
  const [confirmedName, setConfirmedName] = useState('');
  const [now, setNow] = useState(() => new Date());
  const submittingRef = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    ensureStatusDeviceCookie();
  }, []);

  const beginStatus = (newStatus: Status) => {
    const savedName = readGuestNameCookie();
    setGuestName(savedName === 'Guest' ? '' : savedName);
    setGuestEmail('');
    setMood('great');
    setStatus(newStatus);
    submittingRef.current = false;
    setStep('form');
  };

  const submitStatus = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!status || submittingRef.current) return;

    const formData = new FormData(event.currentTarget);
    const submittedName = String(formData.get('name') || '');
    const submittedEmail = String(formData.get('email') || '');
    if (!submittedName.trim()) return;

    const normalizedName = normalizeGuestName(submittedName);
    const normalizedEmail = submittedEmail.trim().toLowerCase().slice(0, GUEST_EMAIL_MAX_LENGTH);
    setGuestName(normalizedName);
    setGuestEmail(normalizedEmail);
    setConfirmedName(normalizedName === 'Guest' ? '' : normalizedName.split(' ')[0]);
    saveGuestNameCookie(normalizedName);
    submittingRef.current = true;
    void postGuestStatus({
      status,
      guestName: normalizedName,
      email: normalizedEmail,
      mood,
    }).catch(() => {
      // Keep the flow fast even if the background notification is unavailable.
    });
    setStep('confirmed');
  };

  const copy = status ? FORM_COPY[status] : null;

  return (
    <div className="status-page">
      <TopoBackground lineColor="62,67,120" />

      <main className="status-inner">
        {step === 'choose' && (
          <section className="status-choose">
            <p className="status-eyebrow">Corazón Townhouse</p>
            <h1 className="status-title">
              You&apos;re <em>here.</em>
            </h1>
            <p className="status-time">{formatDateTime(now)}</p>
            <p className="status-lede">Let us know — one tap.</p>

            <div className="status-cards">
              <button
                type="button"
                aria-label="I've Arrived"
                className="status-card status-card--primary"
                onClick={() => beginStatus('arrived')}
              >
                <span className="status-card-icon" aria-hidden="true">🔑</span>
                <span className="status-card-text">
                  <strong>I&apos;ve arrived</strong>
                  <small>Let us know you made it in</small>
                </span>
                <span className="status-card-arrow" aria-hidden="true">→</span>
              </button>
              <button
                type="button"
                aria-label="I've Departed"
                className="status-card"
                onClick={() => beginStatus('departed')}
              >
                <span className="status-card-icon" aria-hidden="true">👋</span>
                <span className="status-card-text">
                  <strong>I&apos;ve departed</strong>
                  <small>Let us know you&apos;ve headed out</small>
                </span>
                <span className="status-card-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </section>
        )}

        {step === 'form' && status && copy && (
          <form className="status-form-wrap" onSubmit={submitStatus} autoComplete="on" data-status={status}>
            <span className="status-pin"><i aria-hidden="true" />{copy.pin}</span>
            <h1 className="status-title status-title--form">{copy.title}</h1>

            <div className="status-sheet">
              <div className="status-field">
                <label htmlFor="status-name">Name</label>
                <input
                  id="status-name"
                  name="name"
                  type="text"
                  value={guestName}
                  onChange={(event) => setGuestName(event.target.value)}
                  autoComplete="name"
                  autoFocus
                  enterKeyHint="next"
                  maxLength={GUEST_NAME_MAX_LENGTH}
                  required
                />
              </div>

              <div className="status-field">
                <label htmlFor="status-email">Email <span>(optional)</span></label>
                <input
                  id="status-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  value={guestEmail}
                  onChange={(event) => setGuestEmail(event.target.value)}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="next"
                  maxLength={GUEST_EMAIL_MAX_LENGTH}
                  aria-describedby="status-email-note"
                />
                <p id="status-email-note" className="status-field-note">
                  Leave it if you&rsquo;d like to stay connected. We won&rsquo;t add you to any mailing list.
                </p>
              </div>

              <fieldset className="status-moods">
                <legend>{copy.moodLegend}</legend>
                <div>
                  {CHECK_IN_MOOD_OPTIONS.map((option) => {
                    const selected = mood === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-label={option.label}
                        aria-pressed={selected}
                        className={selected ? 'is-selected' : ''}
                        onClick={() => setMood(option.value)}
                      >
                        <span aria-hidden="true">{option.emoji}</span>
                        <small>{option.label}</small>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <button type="submit" className="status-submit">
                {copy.submit}
              </button>
              <button type="button" className="status-back" onClick={() => setStep('choose')}>
                Back
              </button>
            </div>
          </form>
        )}

        {step === 'confirmed' && status && (
          <section className="status-done">
            <span className="status-stamp" aria-hidden="true">
              <b>✓</b>
              <small>{status === 'arrived' ? 'Checked in' : 'Checked out'}</small>
            </span>
            <p className="status-eyebrow">
              {status === 'arrived' ? 'Welcome' : 'Farewell'}{confirmedName ? `, ${confirmedName}` : ''}
            </p>
            <h1 className="status-title status-title--done">
              {status === 'arrived' ? <>You&apos;re <em>checked in.</em></> : <>Thanks for <em>staying with us.</em></>}
            </h1>
            <p className="status-lede">
              {status === 'arrived'
                ? 'Wi-Fi, parking and the house notes are all in your guide.'
                : 'Safe travels — we hope to welcome you back to Capitol Hill.'}
            </p>
            <NextStayOffer status={status} name={guestName} initialEmail={guestEmail} />
            <button type="button" className="status-ghost" onClick={onReturnHome}>
              {status === 'arrived' ? 'Open your guide' : 'Return to Homepage'}
            </button>
          </section>
        )}
      </main>
    </div>
  );
};

export default GuestStatus;
